import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import { fetchStochasticMailDataAction } from "@/modules/administration/features/Stochastic/CampaignJobStatus/slices/campaignJobStatusDataSlice";
import { FetchCampaignJobStatusDataRequest } from "@/modules/administration/features/Stochastic/CampaignJobStatus/api/fetchCampaignJobStatusData/types";
import { fetchBulkBatchStatusDetailsMetadataAction } from "@/modules/administration/features/Stochastic/CampaignJobStatus/slices/bulkBatchStatusDetailsMetadataSlice";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

interface StochasticFilters {
    week?: number;
    year?: number;
    batchStatusId?: number;
}

export function useCampaignJobStatusData() {
  const dispatch = useDispatch();

  const {
    campaignJobStatusDataRows,
    totalCount,
    campaignWeekEventData,
    cliFileStatus,
    batchStatuses,
    loading: fetchCampaignJobStatusDataLoading,
    error: fetchCampaignJobStatusDataError,
  } = useSelector((state: RootState) => state.campaignJobStatusData);

  const {
    bulkBatchStatusDetailsMetadata,
    loading: bulkBatchStatusDetailsMetadataLoading,
    error: bulkBatchStatusDetailsMetadataError,
  } = useSelector((state: RootState) => state.bulkBatchStatusDetailsMetadata);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<Record<string, unknown>>({
    week: 1,
    year: 2025,
    batchStatusId: undefined,
  });

  const requestParams = useMemo<FetchCampaignJobStatusDataRequest>(() => {
    const weekNum = (filters.week as number) ?? 1;
    const yearNum = (filters.year as number) ?? 2025;
    const batchStatusId = filters.batchStatusId as number | undefined;

    return {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : "DESC",
      week: weekNum,
      year: yearNum,
      ...(batchStatusId && { batchStatusId }),
    };
  }, [pagination, sorting, filters]);

  useEffect(() => {
    dispatch(fetchStochasticMailDataAction(requestParams));
    dispatch(fetchBulkBatchStatusDetailsMetadataAction(requestParams));
  }, [dispatch, requestParams]);

  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue) => {
      setPagination((prev) =>
        typeof updaterOrValue === "function"
          ? updaterOrValue(prev)
          : updaterOrValue,
      );
    },
    [],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      setSorting((prev) =>
        typeof updaterOrValue === "function"
          ? updaterOrValue(prev)
          : updaterOrValue,
      );
    },
    [],
  );

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  return {
    campaignJobStatusDataRows,
    mailDataRowsLoading: fetchCampaignJobStatusDataLoading,
    mailDataRowsError: fetchCampaignJobStatusDataError,
    campaignWeekEventData,
    cliFileStatus,
    batchStatuses,
    totalCount,
    pagination,
    sorting,
    filters,
    bulkBatchStatusDetailsMetadata,
    bulkBatchStatusDetailsMetadataLoading,
    bulkBatchStatusDetailsMetadataError,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
